import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  PersonOff,
  DeleteForever,
  Warning,
  Security,
  Info,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { userService } from '../../services/apiServices';

const AccountSettings = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleDeactivateAccount = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await userService.deactivateAccount({
        "deactivate" : true
    });
      if (response.data.success) {
        setSuccess('Account deactivated successfully. You will be logged out.');
        setTimeout(() => {
          logout();
          navigate('/auth/login', { 
            state: { message: 'Your account has been deactivated successfully.' }
          });
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to deactivate account');
      }
    } catch (err) {
      console.error('Deactivate account error:', err);
      const errorMessage = err.response?.data?.message || 'Network error. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
      setDeactivateDialogOpen(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmationText !== 'DELETE MY ACCOUNT') {
      setError('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await userService.deleteAccount();
      if (response.data.success) {
        setSuccess('Account deleted successfully. You will be logged out.');
        setTimeout(() => {
          logout();
          sessionStorage.setItem('accountDeletedAccess', 'true'); // One-time access token
          navigate('/auth/account-deleted', { 
            state: { 
              message: 'Your account has been deleted successfully.',
              fromAccountDeletion: true // Security flag to allow access
            }
          });
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to delete account');
      }
    } catch (err) {
      console.error('Delete account error:', err);
      const errorMessage = err.response?.data?.message || 'Network error. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 3,
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%)',
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold',
            color: 'primary.main',
            mb: 3,
          }}
        >
          Account Settings
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Account Status */}
        <Card sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Security sx={{ color: 'success.main', mr: 2 }} />
              <Typography variant="h6" fontWeight="600">
                Account Status
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip 
                label="Active" 
                color="success" 
                variant="outlined"
                icon={<CheckCircle />}
              />
              <Typography variant="body2" color="text.secondary">
                Your account is currently active and fully functional
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Account Actions */}
        <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          Account Management
        </Typography>

        {/* Deactivate Account */}
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <PersonOff sx={{ color: 'warning.main', mr: 2, mt: 0.5 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  Deactivate Account
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Temporarily disable your account. You can reactivate it anytime by logging in again.
                </Typography>
                
                <List dense sx={{ mb: 2 }}>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Info fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Your profile will be hidden from other users"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Info fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="You won't receive any notifications"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Info fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Your data will be preserved and restored upon reactivation"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                </List>

                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<PersonOff />}
                  onClick={() => setDeactivateDialogOpen(true)}
                  disabled={loading}
                  sx={{ borderRadius: 2 }}
                >
                  Deactivate Account
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        {/* Delete Account */}
        <Card sx={{ mb: 3, borderRadius: 2, borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <DeleteForever sx={{ color: 'error.main', mr: 2, mt: 0.5 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight="600" gutterBottom color="error.main">
                  Delete Account
                </Typography>
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="600">
                    This action is permanent and cannot be undone!
                  </Typography>
                </Alert>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Permanently delete your account and all associated data. This action cannot be reversed.
                </Typography>
                
                <List dense sx={{ mb: 2 }}>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Warning fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="All your data will be permanently deleted"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Warning fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="You will lose access to all your fantasy teams and leagues"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Warning fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Account recovery may be possible only through admin intervention"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                </List>

                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteForever />}
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={loading}
                  sx={{ borderRadius: 2 }}
                >
                  Delete Account
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Deactivate Confirmation Dialog */}
        <Dialog 
          open={deactivateDialogOpen} 
          onClose={() => setDeactivateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonOff sx={{ color: 'warning.main', mr: 1 }} />
              Confirm Account Deactivation
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to deactivate your account?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Your account will be temporarily disabled, but you can reactivate it anytime by logging in again.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setDeactivateDialogOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeactivateAccount}
              color="warning"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <PersonOff />}
            >
              {loading ? 'Deactivating...' : 'Deactivate Account'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DeleteForever sx={{ color: 'error.main', mr: 1 }} />
              Confirm Account Deletion
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="600">
                This action is permanent and cannot be undone!
              </Typography>
            </Alert>
            
            <Typography variant="body1" gutterBottom>
              Are you absolutely sure you want to delete your account?
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              All your data, including fantasy teams, leagues, and preferences will be permanently deleted.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
              Type "DELETE MY ACCOUNT" to confirm:
            </Typography>
            
            <TextField
              fullWidth
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder="DELETE MY ACCOUNT"
              error={confirmationText !== '' && confirmationText !== 'DELETE MY ACCOUNT'}
              helperText={confirmationText !== '' && confirmationText !== 'DELETE MY ACCOUNT' ? 'Text must match exactly' : ''}
              sx={{ mb: 2 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => {
                setDeleteDialogOpen(false);
                setConfirmationText('');
                setError('');
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteAccount}
              color="error"
              variant="contained"
              disabled={loading || confirmationText !== 'DELETE MY ACCOUNT'}
              startIcon={loading ? <CircularProgress size={20} /> : <DeleteForever />}
            >
              {loading ? 'Deleting...' : 'Delete Account'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default AccountSettings;